import {AddSchema, CreateAccessor, GetDoc, GetDocs, MGLClass} from "mobx-graphlink";
import {emptyArray} from "js-vextensions";
import {ClicheBlacklistEntry, EchoCharacter, EchoDifficultyState, EchoHuntModelReasoning, EchoHuntModelRoles, EchoJudgeSettings, EchoSuspicionSettings, EchoWordlistSettings, EchoWorld} from "../../../EchoHunt/Types.js";

export const GetEchoProfile = CreateAccessor((id: string|n): EchoProfile|n=>{
	if (id == null) return null;
	return GetDoc({}, a=>a.echoProfiles.get(id));
});
export const GetEchoProfiles = CreateAccessor((userID: string|n): EchoProfile[]=>{
	if (userID == null) return emptyArray; // if we aren't signed-in, don't even try to make request (the security-rules will make it fail)
	return GetDocs({params: {filter: {creator: userID && {equalTo: userID}}}}, a=>a.echoProfiles);
});

/** One row per Echo Hunt "profile" (§14 `echo_profiles`). Typically one per user, but a user may
 * create several (e.g. to try different difficulty settings against the same journal). */
@MGLClass({table: "echoProfiles"})
export class EchoProfile {
	constructor(data?: Partial<EchoProfile>) { Object.assign(this, data); }

	id: string;
	creator: string;
	createdAt: number;

	/** share of a round's bullets that come from the journal (§9.1, §11) */
	xShare?: number|n;
	/** min miscategorization the judge must reach before a round is accepted (§9.1) */
	yTarget?: number|n;

	/** Y/auc settings + auto-adjust state (§11) */
	difficulty?: EchoDifficultyState|n;

	/** P10: the judge loop's knobs -- the two-sided AUC band, the loop judge models, the repair budget and
	 * the rejection-sampling toggle (§9.1-§9.3, §5.5). Absent means `DefaultEchoJudgeSettings()`, so a
	 * profile created before P10 still judges correctly. */
	judge?: EchoJudgeSettings|n;

	/** P11: the suspicion meter's knobs (§8.4). Absent means `DefaultSuspicionSettings()`, so a profile
	 * created before P11 records AND applies suspicion the default way.
	 *
	 * IMPORTANT: `applied: false` only stops the meter from CHANGING the story (the lucid interlude and the
	 * story's end). The `suspicion` value on each story keeps accumulating either way, so the number is an
	 * honest record and the feature can be switched on later without losing the rounds played in between. */
	suspicion?: EchoSuspicionSettings|n;

	/** P11 follow-up: which story is currently SELECTED. The user may have any number of stories running at
	 * once and jump between them, so "active" is a choice rather than a property of a story row -- putting
	 * `active: true` on the stories would need exactly-one invariants and a bulk update on every switch.
	 *
	 * Absent/unresolvable falls back to the newest non-ended story (`GetActiveStory_Live`), so a profile
	 * created before this field existed behaves exactly as it did. */
	activeStoryId?: string|n;

	/** terms that must never appear in a bullet; P5 flags/neutralizes these (§4.4, §6) */
	bannedTerms?: string[]|n;
	/** in-dream schematic rules the weaver/rewriter must uphold (§8.1) */
	schematicRules?: string[]|n;

	/** the worlds a story can be dropped into (§8.5) */
	worldPool?: EchoWorld[]|n;

	/** the characters the user authored, one of which each story follows (§8.7). A JSON column for the
	 * same reason as `worldPool`: a handful of editable documents needs no table, and a story references
	 * an entry by id so that editing a character affects the running story on purpose. */
	characters?: EchoCharacter[]|n;

	/** user-confirmed cliché phrases, with hit counts (§5.5, §13.3) */
	clicheBlacklist?: ClicheBlacklistEntry[]|n;

	/** per-role model overrides; unset roles fall back to the global model (P3) */
	modelRoles?: EchoHuntModelRoles|n;

	/** per-role REASONING EFFORT overrides (2026-10-21). Unset = "auto": a model whose metadata says reasoning
	 * is mandatory gets its own default effort, every other model keeps the helper's default (disabled). Needed
	 * because some endpoints reject a request that disables reasoning outright. */
	modelReasoning?: EchoHuntModelReasoning|n;

	/** when true, the weaver is told it may reword each bullet entirely, not just bridge between them
	 * (2026-10-21). Off by default: the paraphrase is the reshape step's job, and the weaver has no retention
	 * check to protect an event from being eroded by a heavier rewrite. */
	weaveParaphraseNote?: boolean|n;

	/** cutoffs/tags for the seed-word list the fake generator draws concepts from (§5.2, P6).
	 * Absent means "use `DefaultWordlistSettings()`", so a profile created before this field existed
	 * still filters correctly. */
	wordlist?: EchoWordlistSettings|n;
}
AddSchema("EchoProfile", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},

		xShare: {type: ["null", "number"]},
		yTarget: {type: ["null", "number"]},

		difficulty: {oneOf: [{type: "null"}, {$ref: "EchoDifficultyState"}]},

		judge: {oneOf: [{type: "null"}, {$ref: "EchoJudgeSettings"}]},

		suspicion: {oneOf: [{type: "null"}, {$ref: "EchoSuspicionSettings"}]},

		activeStoryId: {type: ["null", "string"]},

		bannedTerms: {oneOf: [{type: "null"}, {items: {type: "string"}}]},
		schematicRules: {oneOf: [{type: "null"}, {items: {type: "string"}}]},

		worldPool: {oneOf: [{type: "null"}, {items: {$ref: "EchoWorld"}}]},

		characters: {oneOf: [{type: "null"}, {items: {$ref: "EchoCharacter"}}]},

		clicheBlacklist: {oneOf: [{type: "null"}, {items: {$ref: "ClicheBlacklistEntry"}}]},

		modelRoles: {oneOf: [{type: "null"}, {$ref: "EchoHuntModelRoles"}]},
		modelReasoning: {oneOf: [{type: "null"}, {$ref: "EchoHuntModelReasoning"}]},
		weaveParaphraseNote: {type: ["null", "boolean"]},

		wordlist: {oneOf: [{type: "null"}, {$ref: "EchoWordlistSettings"}]},
	},
});
